import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar, faTriangleExclamation, faLightbulb, faRobot, faRotateRight, faUpRightFromSquare, faUserCheck, faListUl,
  faArrowsLeftRight, faBullseye,
} from "@fortawesome/free-solid-svg-icons";
import {
  Alert, Box, Button, Checkbox, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, FormGroup, Grid, Paper, Radio, RadioGroup, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { CircularProgress } from "@mui/material";
import AI_PURPLE from "../../../../assets/images/ai-icon-purple.png";
import NO_REPORT from "../../../../assets/images/no-report.png";
import NOTI_PURPLE from "../../../../assets/images/noti-purple.png";
import CLOCK_IMG from "../../../../assets/images/clock.png";
import {
  AIDetectionPerQuestion, ReportModel,
} from "../../../../model/report.modelt";
import { ResultMultipleChoice } from "../../../../model/summary-multiple-choice.model";
import {
  detectAI, getReportScore, getProfiReportSnapshot, postCustomMeasureCompareScore, postCustomMeasureScore,
  postGeneralObjectiveAnalysis, postProfiReanalyze,
} from "../../../../services/api.service";
import ConfirmGeneralModal from "../../../../modal/confirm-general.modal";
import { ProviderContext } from "../../../../contexts/provider.context";
import CustomSnackbar from "../../../../components/snackbar/snackbar.component";
import { useSnackbar } from "../../../../utils/snackbar.util";
import { isCheckboxQuestion, isMultipleChoiceLike, MULTIPLE_CHOICE } from "../../../../shared/question-type.util";
import "./candidate-report.component.scss";

interface CandidateReportProps {
  reportModel: ReportModel | undefined;
  sectionList: any;
  updateSection(field): void;
  resultMultipleChoice: ResultMultipleChoice[];
  sendEmailAction(email, name, id): void;
  job?: any;
  isLoading?: boolean;
  /** ProFi: จาก interview_candidates เมื่อ interview_report ยังไม่มี app_id PROFI */
  candidateAppId?: string;
  /** เกณฑ์วัดผลร่วมจาก job question (Firestore) — ใช้กับทุกผู้สมัคร */
  jobCustomMeasurementCriteria?: string;
  /** ชุด General Question: แสดงเฉพาะ Transcript / Analyze / Custom Insight */
  generalQuestionSetMinimal?: boolean;
  /** Objective ชุดทั่วไป (job_advance.job_description) — ใช้แสดงในแท็บวิเคราะห์; ฝั่ง API โหลด objective จาก template อีกครั้ง */
  generalObjectiveText?: string;
}

interface GeneralObjectivePerQuestionRow {
  question_id: string;
  question_snippet: string;
  how_answer_relates_to_objective: string;
  score_1_to_10: number;
}

interface GeneralObjectiveAnalysisPayload {
  executive_summary: string;
  alignment_with_objective: string;
  strengths: string[];
  gaps_or_risks: string[];
  recommended_follow_ups: string[];
  per_question: GeneralObjectivePerQuestionRow[];
  overall_score_1_to_10: number;
}

interface ReportScoreAnalysis {
  match_score: number;
  match_score_explanation: string;
  strengths: string[];
  weaknesses: string[];
  next_steps: {
    recommendation: string;
    detailed_advice: string;
    suggested_actions: string[];
  };
}

/** Response analysis จาก POST /custom-measure-score (โครงเดียวกับ check-resume aggregate) */
interface CustomMeasureCriteriaBreakdownItem {
  label: string;
  assessment: string;
  score: number;
}

interface CustomMeasureAnalysis {
  summary: string;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
  match_score: number;
  skills: string[];
  experience_summary: string;
  /** ประเมินแยกตามหัวข้อย่อยในเกณฑ์ (จาก API รุ่นใหม่) */
  criteria_breakdown?: CustomMeasureCriteriaBreakdownItem[];
  criteria_coverage?: { total_topics: number; evidenced_topics: number; coverage_summary: string };
  confidence_score?: number;
  red_flags?: string[];
  hiring_recommendation?: "hire" | "borderline" | "reject";
}

interface CustomMeasureCompareSide {
  is_success: boolean;
  analysis?: CustomMeasureAnalysis;
  error?: string;
  ai_provider: number;
}

interface ProfiReportSnapshot {
  overall_advice_transcript: string;
  feedback_transcript_logs: Array<{
    question_id?: string;
    question?: { th?: string; en?: string };
    feedback?: string;
  }>;
  hiring_score: number | null;
  updated_at?: string | null;
}

/** ให้ payload พร้อม bind กับ UI เหมือน profi-landing result-report */
function normalizeProfiReportData(raw: unknown): ProfiReportSnapshot {
  const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const overall =
    typeof r.overall_advice_transcript === "string"
      ? r.overall_advice_transcript
      : r.overall_advice_transcript != null
        ? String(r.overall_advice_transcript)
        : "";

  const logsRaw = Array.isArray(r.feedback_transcript_logs) ? r.feedback_transcript_logs : [];
  const feedback_transcript_logs = logsRaw.map((item, idx) => {
    const log = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const q =
      log.question && typeof log.question === "object"
        ? (log.question as Record<string, unknown>)
        : {};
    return {
      question_id:
        typeof log.question_id === "string" ? log.question_id : String(log.question_id ?? `q-${idx}`),
      question: {
        th: q.th != null ? String(q.th) : "",
        en: q.en != null ? String(q.en) : "",
      },
      feedback: log.feedback != null ? String(log.feedback) : "",
    };
  });

  let hiring_score: number | null = null;
  const hs = r.hiring_score;
  if (typeof hs === "number" && Number.isFinite(hs)) {
    hiring_score = Math.min(100, Math.max(0, hs));
  } else if (hs != null) {
    const n = Number(hs);
    if (Number.isFinite(n)) hiring_score = Math.min(100, Math.max(0, n));
  }

  return {
    overall_advice_transcript: overall,
    feedback_transcript_logs,
    hiring_score,
    updated_at: r.updated_at != null ? String(r.updated_at) : null,
  };
}

function profiHasDisplayableContent(m: ProfiReportSnapshot): boolean {
  const advice = (m.overall_advice_transcript ?? "").trim();
  if (advice.length > 0) return true;
  if ((m.feedback_transcript_logs ?? []).some((l) => String(l.feedback ?? "").trim().length > 0)) {
    return true;
  }
  if (m.hiring_score != null && Number.isFinite(m.hiring_score)) return true;
  return false;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Normalise overall_risk which may be Thai or English */
function normaliseRisk(risk: string): "HIGH" | "MEDIUM" | "LOW" {
  if (!risk) return "LOW";
  const r = risk.toLowerCase();
  if (r === "high" || r === "สูง") return "HIGH";
  if (r === "medium" || r === "ปานกลาง") return "MEDIUM";
  return "LOW";
}

function getScoreColor(score: number) {
  if (score >= 80) return "#22c55e";
  if (score >= 60) return "#f59e0b";
  return "#ef4444";
}

function scoreLabel(score: number) {
  if (score >= 80) return "ดีมาก";
  if (score >= 60) return "ปานกลาง";
  return "ต้องพัฒนา";
}

function formatTime(totalSeconds: number | string) {
  try {
    const n = typeof totalSeconds === "string" ? parseInt(totalSeconds, 10) || 0 : totalSeconds;
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = n % 60;
    return [h, m, s].map((x) => String(x).padStart(2, "0")).join(":");
  } catch { return "00:00:00"; }
}

/** Transcript log แบบเลือกตอบอาจมี choices จาก API แม้ไม่ได้ประกาศใน type */
type TranscriptLogMc = {
  question_id: string;
  question_type?: string;
  /** Sub-type, e.g. "checkbox" / "multiple_choice_no_answer" */
  answer_type?: string;
  /** UI hint persisted from admin builder, e.g. "checkbox" */
  ui_variant?: string;
  response?: { index?: string | number; value?: string; indexes?: Array<string | number> };
  choices?: Array<{ th?: string; en?: string }>;
};

function parseMcResponseIndex(idx: unknown): number | null {
  if (idx === "" || idx === undefined || idx === null) return null;
  const n = Number(idx);
  if (!Number.isFinite(n) || n === 99) return null;
  return n;
}

/** API บางเส้นส่ง selected_value / correct_value เป็น string หรือ object { th, en } */
function mcFieldToDisplayText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && value !== null) {
    const o = value as Record<string, unknown>;
    const th = typeof o.th === "string" ? o.th.trim() : "";
    const en = typeof o.en === "string" ? o.en.trim() : "";
    if (th && en) return `${th} (${en})`;
    if (th || en) return th || en;
  }
  return String(value).trim();
}

function getMcSelectedAnswerText(log: TranscriptLogMc, mc: ResultMultipleChoice | undefined): string {
  const fromMc = mcFieldToDisplayText(mc?.selected_value as unknown);
  if (fromMc) return fromMc;
  const fromLog = mcFieldToDisplayText(log.response?.value as unknown);
  if (fromLog) return fromLog;
  const i = parseMcResponseIndex(log.response?.index);
  if (i === null) return "—";
  const ch = log.choices?.[i] || mc?.choices?.[i];
  if (!ch) return "—";
  const th = (ch.th ?? "").trim();
  const en = (ch.en ?? "").trim();
  if (th && en) return `${th} (${en})`;
  return th || en || "—";
}

/** คืนข้อความเฉลยเมื่อเป็นคำถาม multiple_choice ที่มี correct_index; ไม่มีเฉลยคืน null */
function getMcCorrectAnswerText(log: TranscriptLogMc, mc: ResultMultipleChoice | undefined): string | null {
  if (log.question_type !== MULTIPLE_CHOICE) return null;
  if (!mc) return null;
  if (mc.correct_index === null || mc.correct_index === undefined) return null;
  const fromMc = mcFieldToDisplayText(mc.correct_value as unknown);
  if (fromMc) return fromMc;
  const ch = mc.choices?.[mc.correct_index] || log.choices?.[mc.correct_index];
  if (!ch) return "—";
  const th = (ch.th ?? "").trim();
  const en = (ch.en ?? "").trim();
  if (th && en) return `${th} (${en})`;
  return th || en || "—";
}

function parseMcSelectedIndex(log: TranscriptLogMc, mc: ResultMultipleChoice | undefined): number | null {
  const fromLog = parseMcResponseIndex(log.response?.index);
  if (fromLog !== null) return fromLog;
  if (mc?.selected_index !== undefined && mc.selected_index !== null && String(mc.selected_index).trim() !== "") {
    const n = Number(mc.selected_index);
    if (Number.isFinite(n) && n !== 99) return n;
  }
  return null;
}

/** สำหรับ checkbox: คืนค่าทุก index ที่ผู้สมัครเลือก (รวมจาก log.response.indexes และ mc.selected_indexes) */
function parseMcSelectedIndexes(
  log: TranscriptLogMc,
  mc: ResultMultipleChoice | undefined,
): number[] {
  const out = new Set<number>();
  const addAll = (arr: Array<string | number> | null | undefined) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((v) => {
      const n = parseMcResponseIndex(v);
      if (n !== null) out.add(n);
    });
  };
  addAll(log.response?.indexes);
  addAll(mc?.selected_indexes ?? null);
  // ถ้ายังว่างแต่มี selected_index เดี่ยว (เช่นรายงานเก่าที่ตอบช่อง checkbox เดียว) ให้ fallback
  if (out.size === 0) {
    const single = parseMcSelectedIndex(log, mc);
    if (single !== null) out.add(single);
  }
  return Array.from(out).sort((a, b) => a - b);
}

function getMcChoicesForDisplay(
  log: TranscriptLogMc,
  mc: ResultMultipleChoice | undefined,
): Array<{ th?: string; en?: string }> {
  const fromLog = log.choices;
  if (fromLog && fromLog.length > 0) return fromLog;
  return mc?.choices ?? [];
}

/** มีเฉลย (ให้แสดงถูก/ผิด) เฉพาะ multiple_choice ที่มี correct_index */
function isMcGradedWithKey(log: TranscriptLogMc, mc: ResultMultipleChoice | undefined): boolean {
  if (log.question_type !== MULTIPLE_CHOICE || !mc) return false;
  if (mc.correct_index === null || mc.correct_index === undefined) return false;
  const ci = Number(mc.correct_index);
  return Number.isFinite(ci);
}

function resolveMcIsCorrect(
  mc: ResultMultipleChoice | undefined,
  selectedIndex: number | null,
  correctIndex: number,
  graded: boolean,
): boolean | null {
  if (!graded) return null;
  if (mc?.isCorrect === true) return true;
  if (mc?.isCorrect === false) return false;
  if (selectedIndex === null) return null;
  return selectedIndex === correctIndex;
}

function McChoiceLabel({ ch }: { ch: { th?: string; en?: string } }) {
  const th = (ch.th ?? "").trim();
  const en = (ch.en ?? "").trim();
  if (!th && !en) return <span className="cr-mc-choice-empty">—</span>;
  return (
    <span className="cr-mc-choice-label">
      {th ? <span className="cr-mc-choice-th">{th}</span> : null}
      {th && en ? <span className="cr-mc-choice-sep"> </span> : null}
      {en ? <span className="cr-mc-choice-en">{th ? `(${en})` : en}</span> : null}
    </span>
  );
}

/** Transcript: แสดงตัวเลือกแบบ radio/checkbox (อ่านอย่างเดียว) + สถานะถูก/ผิดเมื่อมีเฉลย */
function TranscriptMcAnswerBlock({
  log,
  mc,
}: {
  log: TranscriptLogMc;
  mc: ResultMultipleChoice | undefined;
}) {
  const choices = getMcChoicesForDisplay(log, mc);
  const isCheckbox = isCheckboxQuestion({
    question_type: log.question_type,
    answer_type: log.answer_type ?? mc?.answer_type ?? null,
    ui_variant: log.ui_variant ?? mc?.ui_variant ?? null,
  }) || mc?.is_checkbox === true;
  const selectedIndex = parseMcSelectedIndex(log, mc);
  const selectedIndexes = isCheckbox ? parseMcSelectedIndexes(log, mc) : [];
  const graded = !isCheckbox && isMcGradedWithKey(log, mc);
  const correctIndex = graded && mc ? Number(mc.correct_index) : -1;
  const resolvedCorrect = graded && mc
    ? resolveMcIsCorrect(mc, selectedIndex, correctIndex, graded)
    : null;

  if (choices.length === 0) {
    const mcSelectedText = getMcSelectedAnswerText(log, mc);
    const mcCorrectText = getMcCorrectAnswerText(log, mc);
    return (
      <div className="cr-report-candidate-answer">
        <h5>คำตอบที่เลือก</h5>
        <div className="cr-report-answer-content">
          <div className="cr-report-answer-text"><p>{mcSelectedText}</p></div>
        </div>
        {mcCorrectText !== null && (
          <>
            <h5 className="cr-report-mc-correct-title">เฉลย (คำตอบที่ถูก)</h5>
            <div className="cr-report-answer-content">
              <div className="cr-report-answer-text"><p>{mcCorrectText}</p></div>
            </div>
          </>
        )}
      </div>
    );
  }

  if (isCheckbox) {
    return (
      <div className="cr-report-candidate-answer cr-report-mc-block">
        <div className="cr-report-mc-head">
          <h5>คำตอบที่เลือก {selectedIndexes.length > 0 ? `(${selectedIndexes.length} ข้อ)` : ""}</h5>
          <Chip size="small" label="เลือกได้หลายข้อ" variant="outlined" className="cr-report-mc-chip" />
        </div>
        <Box
          className="cr-mc-radio-readonly"
          sx={{ pointerEvents: "none", userSelect: "none" }}
        >
          <FormGroup>
            {choices.map((ch, idx) => {
              const isUserPick = selectedIndexes.includes(idx);
              const rowParts = ["cr-mc-row"];
              if (isUserPick) rowParts.push("cr-mc-row--selected-plain");
              return (
                <div key={idx} className={rowParts.join(" ")}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        disableRipple
                        tabIndex={-1}
                        checked={isUserPick}
                      />
                    }
                    label={<McChoiceLabel ch={ch} />}
                    className="cr-mc-form-label"
                  />
                </div>
              );
            })}
          </FormGroup>
        </Box>
      </div>
    );
  }

  const groupValue = selectedIndex === null ? "" : String(selectedIndex);

  return (
    <div className="cr-report-candidate-answer cr-report-mc-block">
      <div className="cr-report-mc-head">
        <h5>คำตอบที่เลือก</h5>
        {resolvedCorrect === true && (
          <Chip size="small" label="ตอบถูก" color="success" variant="outlined" className="cr-report-mc-chip" />
        )}
        {resolvedCorrect === false && (
          <Chip size="small" label="ตอบผิด" color="error" variant="outlined" className="cr-report-mc-chip" />
        )}
      </div>
      <Box
        className="cr-mc-radio-readonly"
        sx={{ pointerEvents: "none", userSelect: "none" }}
      >
        <RadioGroup value={groupValue} name={`mc-transcript-${log.question_id}`}>
          {choices.map((ch, idx) => {
            const isUserPick = selectedIndex === idx;
            const rowParts = ["cr-mc-row"];
            if (!graded && isUserPick) rowParts.push("cr-mc-row--selected-plain");
            if (graded) {
              if (resolvedCorrect === true && isUserPick) rowParts.push("cr-mc-row--success");
              if (resolvedCorrect === false && isUserPick) rowParts.push("cr-mc-row--wrong");
              if (resolvedCorrect === false && idx === correctIndex) rowParts.push("cr-mc-row--answer-key");
            }
            return (
              <div key={idx} className={rowParts.join(" ")}>
                <FormControlLabel
                  value={String(idx)}
                  control={<Radio size="small" color="primary" disableRipple tabIndex={-1} />}
                  label={<McChoiceLabel ch={ch} />}
                  className="cr-mc-form-label"
                />
              </div>
            );
          })}
        </RadioGroup>
      </Box>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

const ScoreRing = ({ score, label, size = 100 }: { score: number; label: string; size?: number }) => {
  const r = (size / 2) * 0.72;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(score, 100) / 100) * c;
  const color = getScoreColor(score);
  return (
    <div className="cr-ring-wrap">
      <div className="cr-ring-inner" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          <circle className="cr-ring-track" cx={size / 2} cy={size / 2} r={r} />
          <circle
            className="cr-ring-bar"
            cx={size / 2} cy={size / 2} r={r}
            strokeDasharray={c}
            strokeDashoffset={offset}
            style={{ stroke: color, transform: `rotate(-90deg)`, transformOrigin: "center" }}
          />
        </svg>
        <div className="cr-ring-text">
          <span className="cr-ring-num">{Math.round(score)}</span>
          <span className="cr-ring-denom">/ 100</span>
        </div>
      </div>
      <span className="cr-ring-label">{label}</span>
    </div>
  );
};

const HBar = ({ label, score, maxScore = 10 }: { label: string; score: number; maxScore?: number }) => {
  const pct = maxScore > 0 ? Math.min((score / maxScore) * 100, 100) : 0;
  const color = getScoreColor((pct));
  return (
    <div className="cr-hbar-row">
      <span className="cr-hbar-label">{label}</span>
      <div className="cr-hbar-track">
        <div className="cr-hbar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="cr-hbar-val">{score}/{maxScore}</span>
    </div>
  );
};

function CustomMeasureAnalysisDetails({
  criteria,
  analysis,
}: {
  criteria: string;
  analysis: CustomMeasureAnalysis;
}) {
  const a = analysis;
  const cmRaw = Number(a.match_score);
  const cmScore = Number.isFinite(cmRaw) ? Math.min(10, Math.max(0, cmRaw)) : 0;
  const cmScore100 = cmScore * 10;
  const recs = a.recommendations ?? [];
  const recHead = recs.length > 0 ? recs[0] : "";
  const recTail = recs.length > 1 ? recs.slice(1) : [];
  return (
    <div className="cr-insight-body">
    
      <div className="cr-score-row">
        <ScoreRing score={cmScore100} label="Match Score" size={110} />
        <div className="cr-score-meta">
          <span
            className={`cr-score-verdict cr-score-verdict-${
              cmScore100 >= 80 ? "high" : cmScore100 >= 60 ? "mid" : "low"
            }`}
          >
            {scoreLabel(cmScore100)}
          </span>
          {a.summary?.trim() ? <p className="cr-score-explanation">{a.summary}</p> : null}
        </div>
      </div>

      {(a.strengths?.length > 0 || a.weaknesses?.length > 0) && (
        <div className="cr-verdict-grid">
          {a.strengths?.length > 0 && (
            <div className="cr-verdict-card strength">
              <div className="cr-verdict-header">
                <FontAwesomeIcon icon={faStar} />
                <span>จุดแข็ง</span>
              </div>
              <ul className="cr-verdict-list">
                {a.strengths.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {a.weaknesses?.length > 0 && (
            <div className="cr-verdict-card weakness">
              <div className="cr-verdict-header">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span>จุดที่ควรพัฒนา</span>
              </div>
              <ul className="cr-verdict-list">
                {a.weaknesses.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {(a.criteria_breakdown?.length ?? 0) > 0 && (
        <div className="cr-criteria-breakdown-section">
          <div className="cr-criteria-breakdown-header">
            <FontAwesomeIcon icon={faListUl} />
            <span>รายละเอียดตามเกณฑ์ที่กำหนด</span>
          </div>
          <ul className="cr-criteria-breakdown-list">
            {a.criteria_breakdown!.map((row, i) => {
              const dimRaw = Number(row.score);
              const dimScore = Number.isFinite(dimRaw) ? Math.min(10, Math.max(0, dimRaw)) : 0;
              const dimPct = Math.round(dimScore * 10);
              return (
                <li key={i} className="cr-criteria-breakdown-item">
                  <div className="cr-criteria-breakdown-item-head">
                    <span className="cr-criteria-breakdown-label">{row.label}</span>
                    <span
                      className={`cr-criteria-breakdown-score cr-criteria-breakdown-score--${
                        dimPct >= 80 ? "high" : dimPct >= 50 ? "mid" : "low"
                      }`}
                    >
                      {dimPct}%
                    </span>
                  </div>
                  <p className="cr-criteria-breakdown-assessment">{row.assessment}</p>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {(a.skills?.length ?? 0) > 0 && (
        <div className="cr-candidate-traits-section">
          <div className="cr-candidate-traits-header">
            <FontAwesomeIcon icon={faUserCheck} />
            <span>ทักษะ/คุณสมบัติ ที่มีของผู้สมัคร</span>
          </div>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {a.skills!.map((sk, i) => (
              <Chip key={i} size="small" label={sk} variant="outlined" />
            ))}
          </Box>
        </div>
      )}

      {(recHead || recTail.length > 0 || a.experience_summary?.trim()) && (
        <div className="cr-next-steps">
          <div className="cr-next-header">
            <FontAwesomeIcon icon={faLightbulb} />
            <span>คำแนะนำ / ขั้นตอนถัดไป</span>
          </div>
          {recHead ? <span className="cr-next-rec">{recHead}</span> : null}
          {recTail.length > 0 ? (
            <ul className="cr-next-actions">
              {recTail.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : null}
          {a.experience_summary?.trim() ? (
            <p
              className="cr-next-advice"
              style={{ marginTop: recHead || recTail.length > 0 ? 12 : 0 }}
            >
              <strong>สรุปประสบการณ์จากคำตอบ:</strong> {a.experience_summary}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function CustomMeasureCompareColumn({
  title,
  accentColor,
  result,
  criteria,
}: {
  title: string;
  accentColor: string;
  result: CustomMeasureCompareSide;
  criteria: string;
}) {
  const provLabel = result.ai_provider === 1 ? "Gemini" : "OpenAI";
  return (
    <Box
      sx={{
        border: `1px solid ${accentColor}40`,
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: `linear-gradient(135deg, ${accentColor}18 0%, ${accentColor}08 100%)`,
          borderBottom: `1px solid ${accentColor}35`,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: accentColor }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          ai_provider = {result.ai_provider} ({provLabel})
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", maxHeight: "min(70vh, 720px)", p: 1.5 }}>
        {!result.is_success ? (
          <Alert severity="error" sx={{ mb: 0 }}>
            {result.error || "วิเคราะห์ไม่สำเร็จ"}
          </Alert>
        ) : result.analysis ? (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
              {typeof result.analysis.confidence_score === "number" && Number.isFinite(result.analysis.confidence_score) ? (
                <Chip
                  size="small"
                  label={`ความมั่นใจ ${Math.round(result.analysis.confidence_score * 100)}%`}
                  variant="outlined"
                />
              ) : null}
              {result.analysis.hiring_recommendation ? (
                <Chip
                  size="small"
                  color="secondary"
                  variant="outlined"
                  label={`จ้างงาน: ${result.analysis.hiring_recommendation}`}
                />
              ) : null}
              {result.analysis.criteria_coverage?.coverage_summary ? (
                <Chip size="small" variant="outlined" label={result.analysis.criteria_coverage.coverage_summary} />
              ) : null}
              {result.analysis.red_flags && result.analysis.red_flags.length > 0 ? (
                <Chip size="small" color="warning" label={`red flags: ${result.analysis.red_flags.length}`} />
              ) : null}
            </Box>
            <CustomMeasureAnalysisDetails criteria={criteria} analysis={result.analysis} />
          </>
        ) : null}
      </Box>
    </Box>
  );
}

function GeneralObjectiveInsightDisplay({
  objectivePreview,
  analysis,
}: {
  objectivePreview: string;
  analysis: GeneralObjectiveAnalysisPayload;
}) {
  const pq = Array.isArray(analysis.per_question) ? analysis.per_question : [];
  return (
    <Box sx={{ px: 2, pb: 2 }}>
      {objectivePreview.trim() ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Objective ของชุดคำถาม
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {objectivePreview}
          </Typography>
        </Alert>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Chip
          size="small"
          color="primary"
          label={`คะแนนรวมเทียบ Objective: ${
            Number.isFinite(Number(analysis.overall_score_1_to_10))
              ? Number(analysis.overall_score_1_to_10).toFixed(1)
              : "—"
          }/10`}
        />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        สรุปภาพรวม
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
        {analysis.executive_summary}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        ความสอดคล้องกับ Objective
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
        {analysis.alignment_with_objective}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            จุดแข็ง
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {(analysis.strengths || []).map((s, i) => (
              <li key={i}>
                <Typography variant="body2">{s}</Typography>
              </li>
            ))}
          </ul>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            ช่องว่าง / ความเสี่ยง
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {(analysis.gaps_or_risks || []).map((s, i) => (
              <li key={i}>
                <Typography variant="body2">{s}</Typography>
              </li>
            ))}
          </ul>
        </Grid>
      </Grid>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        คำแนะนำต่อยอด
      </Typography>
      <ul style={{ margin: "0 0 16px", paddingLeft: 18 }}>
        {(analysis.recommended_follow_ups || []).map((s, i) => (
          <li key={i}>
            <Typography variant="body2">{s}</Typography>
          </li>
        ))}
      </ul>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        รายข้อ: คำถาม ↔ Objective
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>คำถาม (ย่อ)</TableCell>
              <TableCell>เชื่อมกับ Objective</TableCell>
              <TableCell align="right">คะแนน</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pq.map((row, idx) => (
              <TableRow key={row.question_id || idx}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell sx={{ maxWidth: 220 }}>
                  <Typography variant="caption" sx={{ display: "block", whiteSpace: "pre-wrap" }}>
                    {row.question_snippet}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {row.how_answer_relates_to_objective}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {Number.isFinite(Number(row.score_1_to_10))
                    ? Number(row.score_1_to_10).toFixed(1)
                    : row.score_1_to_10}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const AIConfidenceBar = ({ item, index }: { item: AIDetectionPerQuestion; index: number }) => {
  const pct = Math.round((item.ai_detection.confidence_score ?? 0) * 100);
  const risk = normaliseRisk(item.ai_detection.risk_level);
  const color = risk === "HIGH" ? "#ef4444" : risk === "MEDIUM" ? "#f59e0b" : "#22c55e";
  return (
    <div className="cr-hbar-row">
      <span className="cr-hbar-label cr-hbar-label-sm">Q{index + 1}</span>
      <div className="cr-hbar-track">
        <div className="cr-hbar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="cr-hbar-val" style={{ color }}>{pct}%</span>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

function isProfiAppId(v: string | undefined) {
  return String(v || "").toUpperCase() === "PROFI";
}

/** หน้ารายงาน candidate บน ProFi landing: REACT_APP_PROFI_URL/result-report/{report_id} */
function getProfiCandidatePublicReportUrl(reportId: string): string | null {
  const base = (process.env.REACT_APP_PROFI_URL || "").trim().replace(/\/$/, "");
  if (!base || !reportId) return null;
  return `${base}/result-report/${encodeURIComponent(reportId)}`;
}

const CandidateReport: React.FC<CandidateReportProps> = ({
  reportModel,
  sectionList,
  updateSection,
  resultMultipleChoice,
  sendEmailAction,
  job,
  isLoading = false,
  candidateAppId,
  jobCustomMeasurementCriteria = "",
  generalQuestionSetMinimal = false,
  generalObjectiveText = "",
}) => {
  const [activeTab, setActiveTab] = useState<
    | "insight"
    | "overview"
    | "transcript"
    | "analyze"
    | "job_matching"
    | "custom_insight"
    | "general_objective"
    | "profi"
  >("insight");
  const [reportScoreData, setReportScoreData] = useState<ReportScoreAnalysis | null>(null);
  const [isLoadingScore, setIsLoadingScore] = useState(false);
  const [profiReportData, setProfiReportData] = useState<ProfiReportSnapshot | null>(null);
  const [profiSnapshotLoading, setProfiSnapshotLoading] = useState(false);
  const [profiSnapshotStatus, setProfiSnapshotStatus] = useState<
    "idle" | "ready" | "error" | "missing" | "processing"
  >("idle");
  const [profiSnapshotError, setProfiSnapshotError] = useState<string | null>(null);
  const [isReanalyzingProfi, setIsReanalyzingProfi] = useState(false);
  const [isShowSendDialog, setIsShowSendDialog] = useState(false);
  const [isShowAIDetectDialog, setIsShowAIDetectDialog] = useState(false);
  const [isDetectingAI, setIsDetectingAI] = useState(false);
  const [customMeasureLoading, setCustomMeasureLoading] = useState(false);
  const [customMeasureResult, setCustomMeasureResult] = useState<{
    criteria: string;
    analysis: CustomMeasureAnalysis;
  } | null>(null);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [comparePayload, setComparePayload] = useState<{
    criteria: string;
    gemini: CustomMeasureCompareSide;
    openai: CustomMeasureCompareSide;
  } | null>(null);
  const [generalObjectiveLoading, setGeneralObjectiveLoading] = useState(false);
  const [generalObjectiveResult, setGeneralObjectiveResult] = useState<GeneralObjectiveAnalysisPayload | null>(null);
  const loadedReportIdRef = useRef<string | null>(null);
  const loadedCustomMeasureReportIdRef = useRef<string | null>(null);
  const loadedGeneralObjectiveReportIdRef = useRef<string | null>(null);
  const { profileModel }: any = useContext(ProviderContext);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const isCompleted = reportModel?.interview_status === "COMPLETED";
  const hasReport = !!reportModel?.report;
  const isProfiJob =
    !generalQuestionSetMinimal &&
    (isProfiAppId(reportModel?.app_id) || isProfiAppId(candidateAppId));

  const showGeneralObjectiveTab = useMemo(
    () => generalQuestionSetMinimal && String(generalObjectiveText || "").trim().length > 0,
    [generalQuestionSetMinimal, generalObjectiveText],
  );

  // Check if score section has real data
  const s = reportModel?.score;
  const hasScoreData = !!(s && (
    (s.job_fit_score > 0)
    || (s.skill_fit_score?.skills?.length > 0)
    || (s.experience_fit_score?.work_experiences?.length > 0)
    || (s.multiple_choice_score?.total_questions > 0)
  ));

  const profiPublicReportUrl =
    reportModel?.report_id != null
      ? getProfiCandidatePublicReportUrl(reportModel.report_id)
      : null;

  useEffect(() => {
    if (!(jobCustomMeasurementCriteria ?? "").trim()) {
      setCustomMeasureResult(null);
      loadedCustomMeasureReportIdRef.current = null;
    }
  }, [jobCustomMeasurementCriteria]);

  // Single effect: reset then load score — avoids race between two separate effects
  useEffect(() => {
    // Reset first
    setReportScoreData(null);
    setIsLoadingScore(false);
    setActiveTab(generalQuestionSetMinimal ? "transcript" : "insight");
    loadedReportIdRef.current = null;
    setProfiReportData(null);
    setProfiSnapshotLoading(false);
    setProfiSnapshotStatus("idle");
    setProfiSnapshotError(null);
    setIsReanalyzingProfi(false);
    setCustomMeasureLoading(false);
    setCustomMeasureResult(null);
    loadedCustomMeasureReportIdRef.current = null;
    setGeneralObjectiveLoading(false);
    setGeneralObjectiveResult(null);
    loadedGeneralObjectiveReportIdRef.current = null;

    if (!hasReport || !reportModel?.report_id) return;

    const profiHere =
      !generalQuestionSetMinimal &&
      (isProfiAppId(reportModel.app_id) || isProfiAppId(candidateAppId));
    if (profiHere) {
      setProfiSnapshotLoading(true);
      getProfiReportSnapshot(reportModel.report_id)
        .then((res) => {
          const d = res.data as {
            is_success?: boolean;
            data?: ProfiReportSnapshot;
            reason?: string;
            status?: string;
            error?: string | null;
          };
          if (d?.is_success === true && d?.data) {
            setProfiReportData(normalizeProfiReportData(d.data));
            setProfiSnapshotStatus("ready");
            setProfiSnapshotError(null);
          } else if (d?.status === "PROCESSING") {
            setProfiSnapshotStatus("processing");
            setProfiSnapshotError("กำลังประมวลผล ProFi — ลองกดวิเคราะห์ใหม่ภายหลัง หรือรอสักครู่");
          } else if (d?.reason === "no_profi_doc") {
            setProfiSnapshotStatus("missing");
            setProfiSnapshotError("ยังไม่มีการวิเคราะห์ ProFi — รอระบบประมวลผลหลังสัมภาษณ์จบ หรือกดวิเคราะห์ใหม่");
          } else if (d?.reason === "not_profi") {
            setProfiSnapshotStatus("missing");
            setProfiSnapshotError("ยังไม่ระบุเป็น ProFi ในรายงาน — รีเฟรชหน้า หรือตรวจสอบว่าผู้สมัครมาจาก ProFi");
          } else {
            setProfiSnapshotStatus("error");
            setProfiSnapshotError(
              d?.error || "ไม่สามารถโหลดรายงาน ProFi ได้ (เช่น Recommend API error)"
            );
          }
        })
        .catch(() => {
          setProfiSnapshotStatus("error");
          setProfiSnapshotError("โหลดข้อมูล ProFi ไม่สำเร็จ");
        })
        .finally(() => setProfiSnapshotLoading(false));
    }

    if (!generalQuestionSetMinimal) {
      // Prefer data already embedded in reportModel (GET /report/:id response)
      const existing = (reportModel as any)?.report_score?.analysis;
      if (existing) {
        setReportScoreData(existing);
        return;
      }
      loadedReportIdRef.current = reportModel.report_id;
      setIsLoadingScore(true);
      getReportScore(reportModel.report_id, job ?? null)
        .then((res) => {
          if (res.data?.is_success && res.data?.data?.analysis) {
            setReportScoreData(res.data.data.analysis);
          }
        })
        .catch(() => {})
        .finally(() => setIsLoadingScore(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportModel?.report_id, hasReport, reportModel?.app_id, candidateAppId, generalQuestionSetMinimal]);

  useEffect(() => {
    if (!generalQuestionSetMinimal) return;
    const allowed = ["transcript", "analyze", "custom_insight"];
    if (showGeneralObjectiveTab) allowed.push("general_objective");
    setActiveTab((tab) => (allowed.includes(tab) ? tab : "transcript"));
  }, [generalQuestionSetMinimal, showGeneralObjectiveTab]);

  const cd = reportModel?.candidate_detail;
  const candidateInitials = cd
    ? ((cd.firstname?.[0] ?? "") + (cd.lastname?.[0] ?? "")).toUpperCase() || "?"
    : "?";

  /** ProFi: แสดงแบบเดียวกับหน้า profi-landing /public/profi */
  const profiDisplayLogs = profiReportData?.feedback_transcript_logs ?? [];
  const profiOverallTrim = (profiReportData?.overall_advice_transcript ?? "").trim();
  const profiHiringDisplay =
    profiReportData?.hiring_score != null && Number.isFinite(profiReportData.hiring_score)
      ? profiReportData.hiring_score
      : null;

  const handleProfiReanalyze = () => {
    if (!reportModel?.report_id) return;
    setIsReanalyzingProfi(true);
    postProfiReanalyze(reportModel.report_id)
      .then(async (res) => {
        const d = res.data as {
          is_success?: boolean;
          error?: string;
          message?: string;
        };
        if (d.is_success) {
          showSnackbar(
            d.message ||
              "วิเคราะห์สำเร็จ (ไม่ส่งอีเมลถึงผู้สมัคร — อีเมลแจ้งเฉพาะเมื่อสัมภาษณ์จบจากระบบ)",
            "success"
          );
          const snap = await getProfiReportSnapshot(reportModel.report_id);
          const sd = snap.data as {
            is_success?: boolean;
            data?: ProfiReportSnapshot;
            error?: string | null;
          };
          if (sd?.is_success && sd?.data) {
            setProfiReportData(normalizeProfiReportData(sd.data));
            setProfiSnapshotStatus("ready");
            setProfiSnapshotError(null);
          } else {
            setProfiSnapshotStatus("error");
            setProfiSnapshotError(sd?.error || "ยังไม่พร้อมแสดงผล");
          }
        } else {
          showSnackbar(d.error || "วิเคราะห์ไม่สำเร็จ", "error");
          setProfiSnapshotError(d.error || null);
          setProfiSnapshotStatus("error");
        }
      })
      .catch(() => {
        showSnackbar("เกิดข้อผิดพลาด", "error");
      })
      .finally(() => setIsReanalyzingProfi(false));
  };

  const handleSendEmail = () => {
    setIsShowSendDialog(false);
    if (reportModel) {
      sendEmailAction(
        reportModel.candidate_detail.email,
        `${reportModel.candidate_detail.firstname} ${reportModel.candidate_detail.lastname}`,
        reportModel.candidate_detail.candidate_id,
      );
    }
  };

  const handleDetectAI = async () => {
    setIsShowAIDetectDialog(false);
    if (!reportModel?.report_id) { showSnackbar("ไม่พบ Report ID", "error"); return; }
    setIsDetectingAI(true);
    try {
      const res = await detectAI(reportModel.report_id);
      if (res.data?.is_success) {
        showSnackbar("ตรวจสอบการใช้ AI สำเร็จ! ระบบได้วิเคราะห์คำตอบเรียบร้อยแล้ว", "success", 6000);
        setTimeout(() => window.location.reload(), 2000);
      } else {
        showSnackbar(res.data?.error || "เกิดข้อผิดพลาดในการตรวจสอบ AI", "error");
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || err.message || "เกิดข้อผิดพลาดในการตรวจสอบ AI", "error");
    } finally {
      setIsDetectingAI(false);
    }
  };

  const runCustomMeasureAnalysis = async (options?: { switchTabOnSuccess?: boolean; force?: boolean }) => {
    const trimmedJobCriteria = (jobCustomMeasurementCriteria ?? "").trim();
    if (!trimmedJobCriteria) {
      showSnackbar(
        "ยังไม่มีเกณฑ์การวัดผล — กำหนดที่ส่วนหัวของตำแหน่งนี้ (การวัดผลร่วม / Custom Insight)",
        "error",
      );
      return;
    }
    if (!reportModel?.report_id) {
      showSnackbar("ไม่พบ Report ID", "error");
      return;
    }
    setCustomMeasureLoading(true);
    try {
      const res = await postCustomMeasureScore(reportModel.report_id, {
        force: options?.force === true,
      });
      const d = res.data as {
        is_success?: boolean;
        data?: { measurement_criteria?: string; analysis?: CustomMeasureAnalysis };
        error?: string;
      };
      if (d?.is_success && d?.data?.analysis) {
        setCustomMeasureResult({
          criteria: d.data.measurement_criteria ?? trimmedJobCriteria,
          analysis: d.data.analysis,
        });
        loadedCustomMeasureReportIdRef.current = reportModel.report_id;
        if (options?.switchTabOnSuccess) {
          setActiveTab("custom_insight");
        }
        showSnackbar("วิเคราะห์ตามเกณฑ์ของคุณสำเร็จ", "success");
      } else {
        showSnackbar(d?.error || "วิเคราะห์ไม่สำเร็จ", "error");
      }
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message || "เกิดข้อผิดพลาด";
      if (status === 402) {
        showSnackbar("เครดิตไม่เพียงพอ — ไม่สามารถวิเคราะห์ได้", "error");
      } else {
        showSnackbar(msg, "error");
      }
    } finally {
      setCustomMeasureLoading(false);
    }
  };

  useEffect(() => {
    const reportId = reportModel?.report_id;
    const trimmedJobCriteria = (jobCustomMeasurementCriteria ?? "").trim();
    if (!hasReport || !reportId || !trimmedJobCriteria) return;
    if (customMeasureLoading || customMeasureResult) return;
    if (loadedCustomMeasureReportIdRef.current === reportId) return;

    loadedCustomMeasureReportIdRef.current = reportId;
    setCustomMeasureLoading(true);
    postCustomMeasureScore(reportId, { force: false })
      .then((res) => {
        const d = res.data as {
          is_success?: boolean;
          data?: { measurement_criteria?: string; analysis?: CustomMeasureAnalysis };
        };
        if (d?.is_success && d?.data?.analysis) {
          setCustomMeasureResult({
            criteria: d.data.measurement_criteria ?? trimmedJobCriteria,
            analysis: d.data.analysis,
          });
        }
      })
      .catch(() => {
        // Silent on initial load: user can still click "วิเคราะห์" manually.
      })
      .finally(() => setCustomMeasureLoading(false));
  }, [
    reportModel?.report_id,
    hasReport,
    jobCustomMeasurementCriteria,
    customMeasureLoading,
    customMeasureResult,
  ]);

  const runGeneralObjectiveAnalysis = async (options?: { switchTabOnSuccess?: boolean; force?: boolean }) => {
    if (!reportModel?.report_id) {
      showSnackbar("ไม่พบ Report ID", "error");
      return;
    }
    if (!showGeneralObjectiveTab) return;
    setGeneralObjectiveLoading(true);
    try {
      const res = await postGeneralObjectiveAnalysis(reportModel.report_id, {
        force: options?.force === true,
      });
      const d = res.data as {
        is_success?: boolean;
        data?: { analysis?: GeneralObjectiveAnalysisPayload };
        error?: string;
        message?: string;
      };
      if (d?.is_success && d?.data?.analysis) {
        setGeneralObjectiveResult(d.data.analysis);
        loadedGeneralObjectiveReportIdRef.current = reportModel.report_id;
        if (options?.switchTabOnSuccess) setActiveTab("general_objective");
        showSnackbar(d.message || "วิเคราะห์ Objective สำเร็จ", "success");
      } else {
        showSnackbar(d?.error || "วิเคราะห์ Objective ไม่สำเร็จ", "error");
      }
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message || "เกิดข้อผิดพลาด";
      if (status === 429) {
        showSnackbar(msg || "ระบบกำลังวิเคราะห์อยู่ — รอสักครู่แล้วลองใหม่", "error");
      } else {
        showSnackbar(msg, "error");
      }
    } finally {
      setGeneralObjectiveLoading(false);
    }
  };

  useEffect(() => {
    const reportId = reportModel?.report_id;
    if (!hasReport || !reportId || !showGeneralObjectiveTab) return;
    if (generalObjectiveLoading || generalObjectiveResult) return;
    if (loadedGeneralObjectiveReportIdRef.current === reportId) return;

    loadedGeneralObjectiveReportIdRef.current = reportId;
    setGeneralObjectiveLoading(true);
    postGeneralObjectiveAnalysis(reportId, { force: false })
      .then((res) => {
        const d = res.data as {
          is_success?: boolean;
          data?: { analysis?: GeneralObjectiveAnalysisPayload };
        };
        if (d?.is_success && d?.data?.analysis) {
          setGeneralObjectiveResult(d.data.analysis);
        } else {
          loadedGeneralObjectiveReportIdRef.current = null;
        }
      })
      .catch(() => {
        loadedGeneralObjectiveReportIdRef.current = null;
      })
      .finally(() => setGeneralObjectiveLoading(false));
  }, [
    reportModel?.report_id,
    hasReport,
    showGeneralObjectiveTab,
    generalObjectiveLoading,
    generalObjectiveResult,
  ]);

  const handleCustomMeasureReanalyze = () => {
    void runCustomMeasureAnalysis({ force: true });
  };

  const runCustomMeasureCompare = async () => {
    const trimmedJobCriteria = (jobCustomMeasurementCriteria ?? "").trim();
    if (!trimmedJobCriteria) {
      showSnackbar(
        "ยังไม่มีเกณฑ์การวัดผล — กำหนดที่ส่วนหัวของตำแหน่งนี้ (การวัดผลร่วม / Custom Insight)",
        "error",
      );
      return;
    }
    if (!reportModel?.report_id) {
      showSnackbar("ไม่พบ Report ID", "error");
      return;
    }
    setCompareLoading(true);
    setComparePayload(null);
    try {
      const res = await postCustomMeasureCompareScore(reportModel.report_id);
      const d = res.data as {
        is_success?: boolean;
        data?: {
          measurement_criteria?: string;
          gemini?: CustomMeasureCompareSide;
          openai?: CustomMeasureCompareSide;
        };
        error?: string;
      };
      const g = d?.data?.gemini;
      const o = d?.data?.openai;
      if (g != null && o != null) {
        setComparePayload({
          criteria: d.data!.measurement_criteria ?? trimmedJobCriteria,
          gemini: g,
          openai: o,
        });
        setCompareDialogOpen(true);
        if (!d.is_success) {
          showSnackbar(
            d.error || "เปรียบเทียบแล้ว แต่บางฝั่งล้มเหลว — ดูรายละเอียดใน popup",
            "error",
            5500,
          );
        } else {
          showSnackbar("เปรียบเทียบผล Gemini กับ OpenAI สำเร็จ", "success");
        }
      } else {
        showSnackbar(d?.error || "เปรียบเทียบไม่สำเร็จ", "error");
      }
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message || "เกิดข้อผิดพลาด";
      if (status === 402) {
        showSnackbar("เครดิตไม่เพียงพอ — ไม่สามารถโหลดรายงานเพื่อเปรียบเทียบได้", "error");
      } else {
        showSnackbar(msg, "error");
      }
    } finally {
      setCompareLoading(false);
    }
  };

  const compareDisabled =
    compareLoading ||
    !reportModel?.report_id ||
    !(jobCustomMeasurementCriteria ?? "").trim();

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="cr-skeleton-wrap section-control">
        <div className="topic-control">
          <div className="cr-skel cr-skel-icon" />
          <div className="cr-skel cr-skel-title" />
        </div>
        <div className="cr-skeleton-body">
          <div className="cr-skel-card">
            <div className="cr-skel cr-skel-head" />
            <div className="cr-skel cr-skel-line" />
            <div className="cr-skel cr-skel-line cr-skel-line-short" />
          </div>
          <div className="cr-skel-card">
            <div className="cr-skel cr-skel-head" />
            <div className="cr-skel cr-skel-line" />
            <div className="cr-skel cr-skel-line" />
            <div className="cr-skel cr-skel-line cr-skel-line-short" />
          </div>
          <div className="cr-skel-card">
            <div className="cr-skel cr-skel-head" />
            <div className="cr-skel cr-skel-line" />
            <div className="cr-skel cr-skel-line cr-skel-line-short" />
          </div>
        </div>
      </div>
    );
  }

  // ── Not started ───────────────────────────────────────────────────────────
  if (!isCompleted) {
    return (
      <>
        <div className="section-control">
          <div className="topic-control">
            <img src={AI_PURPLE} className="topic-icon" alt="" />
            <span className="topic-name">รายงานผลสัมภาษณ์</span>
          </div>
         
        </div>
        {isShowSendDialog && (
          <ConfirmGeneralModal
            icon={NOTI_PURPLE}
            buttonCancel="ยกเลิก" buttonConfirm="ยืนยัน"
            message="ยืนยันที่จะส่งแจ้งเตือนผู้สมัคร?"
            message2="ระบบจะทำการส่งลิงก์การสัมภาษณ์ให้ผู้สมัครอีกครั้ง ยืนยันที่จะส่ง?"
            onConfrim={handleSendEmail}
            isOpen={isShowSendDialog} onClose={() => setIsShowSendDialog(false)}
          />
        )}
      </>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="cr-wrap section-control">
        {/* Header */}
        <div className="topic-control">
          <img src={AI_PURPLE} className="topic-icon" alt="" />
          <span className="topic-name">รายงานผลสัมภาษณ์</span>
        </div>

        {/* Processing */}
        {!hasReport && (
          <div className="cr-processing">
            <CircularProgress size={28} sx={{ color: "#4A25E1" }} />
            <span className="cr-processing-text">กำลังประมวลผลการสัมภาษณ์...</span>
          </div>
        )}

        {hasReport && reportModel && (
          <>
            {/* Duration badge */}
            <div className="cr-duration-badge">
              <img src={CLOCK_IMG} className="cr-clock-icon" alt="" />
              <span>
                ใช้เวลาสัมภาษณ์ทั้งหมด&nbsp;
                <strong>{formatTime(reportModel.transcript.total_duration)}</strong>
              </span>
            </div>

            <Box className="cr-tabs-box">
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                className="cr-tabs"
              >
                {!generalQuestionSetMinimal && (
                  <>
                    <Tab label="AI Insight" value="insight" />
                    <Tab icon={<AssessmentIcon />} iconPosition="start" label="Overview" value="overview" />
                  </>
                )}
                <Tab icon={<PersonIcon />} iconPosition="start" label="Transcript" value="transcript" />
                <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Analyze" value="analyze" />
                {!generalQuestionSetMinimal && <Tab label="Job Matching Score" value="job_matching" />}
                <Tab label="Custom Insight" value="custom_insight" />
                {showGeneralObjectiveTab && (
                  <Tab
                    icon={<FontAwesomeIcon icon={faBullseye} style={{ fontSize: 15 }} />}
                    iconPosition="start"
                    label="วิเคราะห์ Objective"
                    value="general_objective"
                  />
                )}
                {isProfiJob && (
                  <Tab label="ProFi" value="profi" sx={{ fontWeight: 600 }} />
                )}
              </Tabs>
            </Box>

            {/* ══════════════════════════════════════════════════════════════
                Tab: AI INSIGHT
                • Match Score (ring + strengths/weaknesses/next_steps)
                • Resume summary
                • AI Use Detection (moved here)
            ══════════════════════════════════════════════════════════════ */}
            {!generalQuestionSetMinimal && activeTab === "insight" && (
              <div className="cr-tab-content">

                {/* Resume summary ────────────────────────────────────── */}
                {reportModel.candidate_detail?.resume?.summary && (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <span className="cr-card-title">สรุปเรซูเม่</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-resume-body">
                      <p className="cr-resume-text">{reportModel.candidate_detail.resume.summary}</p>
                      {((reportModel.candidate_detail.resume as any).skills?.length ?? 0) > 0 && (
                        <div className="cr-skills-row">
                          {(reportModel.candidate_detail.resume as any).skills!.map((sk: string, i: number) => (
                            <span key={i} className="cr-skill-tag">{sk}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* AI Use Detection ──────────────────────────────────── */}
                <div className="cr-card">
                  <div className="cr-card-header">
                    <span className="cr-card-title">ตรวจสอบการใช้ AI</span>
                  </div>
                  <Divider className="cr-divider" />
                  <div className="cr-ai-detect-body">
                    {/* Result badge (if already run) */}
                    {reportModel.ai_detection ? (
                      <>
                        {(() => {
                          const risk = normaliseRisk(reportModel.ai_detection.overall_risk);
                          return (
                            <div className={`cr-ai-badge cr-ai-badge-${risk.toLowerCase()}`}>
                              {risk === "HIGH"
                                ? "⚠️ ความเสี่ยงสูง — อาจใช้ AI"
                                : risk === "MEDIUM"
                                ? "🔶 ความเสี่ยงปานกลาง"
                                : "✅ ความเสี่ยงต่ำ — ไม่พบการใช้ AI"}
                            </div>
                          );
                        })()}
                        
                        {reportModel.ai_detection.summary && (
                          <p className="cr-ai-analysis">{reportModel.ai_detection.summary}</p>
                        )}

                        {/* Per-question confidence chart */}
                        {reportModel.ai_detection.per_question?.length > 0 && (
                          <div className="cr-chart-section">
                            <span className="cr-chart-title">ความเสี่ยงรายข้อ (AI Confidence %)</span>
                            {reportModel.ai_detection.per_question.map((item, i) => (
                              <AIConfidenceBar key={item.question_id} item={item} index={i} />
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="cr-ai-detect-desc">
                        ระบบจะวิเคราะห์คำตอบของผู้สมัครจากข้อมูลในรายงานเพื่อตรวจสอบว่าผู้สมัครใช้ AI ช่วยตอบคำถามหรือไม่
                      </p>
                    )}

                    <Button
                      className="button-std cr-btn-ai-detect"
                      onClick={() => setIsShowAIDetectDialog(true)}
                      disabled={isDetectingAI}
                    >
                      <FontAwesomeIcon icon={faRobot} style={{ marginRight: 8 }} />
                      {isDetectingAI ? "กำลังตรวจสอบ..." : reportModel.ai_detection ? "ตรวจสอบอีกครั้ง" : "ตรวจสอบการใช้ AI"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Tab: OVERVIEW (report-modal structure)
            ══════════════════════════════════════════════════════════════ */}
            {!generalQuestionSetMinimal && activeTab === "overview" && (
              <div className="cr-tab-content cr-overview-tab">
                <div className="cr-report-candidate-info">
                  <div className="cr-report-candidate-header">
                    <div className="cr-report-candidate-avatar">
                      <PersonIcon />
                    </div>
                    <div className="cr-report-candidate-details">
                      <h3>{reportModel.candidate_detail?.firstname || ""} {reportModel.candidate_detail?.lastname || ""}</h3>
                      <p className="cr-report-job-title">{(reportModel.candidate_detail as any)?.jobTitle || "—"}</p>
                      <p className="cr-report-company">{reportModel.job_position?.name || reportModel.job_position?.job_title || "—"}</p>
                    </div>
                  </div>
                  <div className="cr-report-candidate-stats">
                    <div className="cr-report-stat">
                      <span className="cr-report-label">Email:</span>
                      <span>{reportModel.candidate_detail?.email || "—"}</span>
                    </div>
                    <div className="cr-report-stat">
                      <span className="cr-report-label">Phone:</span>
                      <span>{reportModel.candidate_detail?.phone || "—"}</span>
                    </div>
                    <div className="cr-report-stat">
                      <span className="cr-report-label">Experience:</span>
                      <span>{(reportModel.candidate_detail as any)?.jobExp ?? 0} years</span>
                    </div>
                  </div>
                </div>
                <div className="cr-report-resume-section">
                  <h3>Resume Summary</h3>
                  <p>{reportModel.candidate_detail?.resume?.summary || "No resume summary available"}</p>
                  <div className="cr-report-skills-section">
                    <h4>Skills</h4>
                    <div className="cr-report-skills-list">
                      {((reportModel.candidate_detail?.resume as any)?.skills as string[] | undefined)?.length
                        ? (reportModel.candidate_detail.resume as any).skills.map((skill: string, index: number) => (
                            <span key={index} className="cr-report-skill-tag">{skill}</span>
                          ))
                        : <span className="cr-report-no-skills">No skills listed</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Tab: TRANSCRIPT (report-modal structure)
            ══════════════════════════════════════════════════════════════ */}
            {activeTab === "transcript" && (
              <div className="cr-tab-content cr-transcript-tab">
                <div className="cr-report-transcript-header">
                  <h3>Interview Transcript</h3>
                  <div className="cr-report-transcript-stats">
                    <span>Duration: {formatTime(reportModel.transcript?.total_duration ?? 0)}</span>
                    <span>Questions: {reportModel.transcript?.logs?.length ?? 0}</span>
                  </div>
                </div>
                <div className="cr-report-transcript-logs">
                  {reportModel.transcript?.logs?.map((log, index) => {
                    const answer = reportModel.transcript?.answers?.find((a) => a.question_id === log.question_id);
                    const mcResult = resultMultipleChoice.find((r) => r.question_id === log.question_id);
                    const isMc = isMultipleChoiceLike(log.question_type);
                    const logMc = log as TranscriptLogMc;
                    return (
                      <div key={log.question_id} className="cr-report-transcript-log">
                        <div className="cr-report-question-header">
                          <span className="cr-report-question-number">Q{index + 1}</span>
                          <span className="cr-report-question-type">{log.topic || "—"}</span>
                          <span className="cr-report-question-duration">{log.duration_start || "—"} - {log.duration_end || "—"}</span>
                        </div>
                        <div className="cr-report-question-content">
                          <h4>{log.question?.th || "Question"}</h4>
                          <p className="cr-report-question-en">{log.question?.en || ""}</p>
                        </div>
                        {isMc && (
                          <TranscriptMcAnswerBlock log={logMc} mc={mcResult} />
                        )}
                        {!isMc && answer && (
                          <div className="cr-report-candidate-answer">
                            <h5>Candidate&apos;s Response:</h5>
                            <div className="cr-report-answer-content">
                              <div className="cr-report-answer-text"><p>{answer.text}</p></div>
                              {log.snapshot && (
                                <div className="cr-report-answer-snapshot">
                                  <img src={log.snapshot} alt="Candidate during interview" className="cr-report-snapshot-image" />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {log.audio_url && (
                          <div className="cr-report-media-section">
                            <h5>Audio Response</h5>
                            <audio controls className="cr-report-audio-player" src={log.audio_url}>Your browser does not support the audio element.</audio>
                          </div>
                        )}
                        {log.video_url && (
                          <div className="cr-report-media-section">
                            <h5>Video Response</h5>
                            <video
                              controls
                              preload="metadata"
                              className="cr-report-audio-player"
                              src={String(log.video_url).split("&token")[0]}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }) ?? (
                    <div className="cr-report-empty-transcript"><p>No transcript data available</p></div>
                  )}
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Tab: ANALYZE (report-modal structure)
            ══════════════════════════════════════════════════════════════ */}
            {activeTab === "analyze" && (
              <div className="cr-tab-content cr-transcript-tab">
                <div className="cr-report-transcript-header">
                  <h3>Interview Analysis</h3>
                  <div className="cr-report-transcript-stats">
                    <span>Duration: {formatTime(reportModel.transcript?.total_duration ?? 0)}</span>
                    <span>Questions: {reportModel.transcript?.logs?.length ?? 0}</span>
                  </div>
                </div>
                <div className="cr-report-transcript-logs" style={{ marginBottom: "24px" }}>
                  <div className="cr-report-transcript-log">
                    <div className="cr-report-question-header">
                      <h3 className="cr-report-question-number">Summary Analysis</h3>
                    </div>
                    <div className="cr-report-question-content">
                      <p className="cr-report-question-en">{reportModel.report?.all_summary_transcript || "No summary analysis available"}</p>
                    </div>
                  </div>
                </div>
                <div className="cr-report-transcript-logs">
                  {reportModel.transcript?.logs?.map((log, index) => {
                    const answerAnalyze = reportModel.report?.summary_transcript_logs?.find((a) => a.question_id === log.question_id);
                    return (
                      <div key={log.question_id} className="cr-report-transcript-log">
                        <div className="cr-report-question-header">
                          <span className="cr-report-question-number">Q{index + 1}</span>
                          <span className="cr-report-question-type">{log.topic || "—"}</span>
                          <span className="cr-report-question-duration">{log.duration_start || "—"} - {log.duration_end || "—"}</span>
                        </div>
                        <div className="cr-report-question-content">
                          <h4>{log.question?.th || "Question"}</h4>
                          <p className="cr-report-question-en">{log.question?.en || ""}</p>
                        </div>
                        {answerAnalyze && (
                          <div className="cr-report-candidate-answer">
                            <h5>Response Analysis:</h5>
                            <div className="cr-report-answer-content">
                              <div className="cr-report-answer-text"><p>{answerAnalyze.summary}</p></div>
                            </div>
                          </div>
                        )}
                        {log.emotion && (
                          <div className="cr-report-emotion-analysis">
                            <h5>Interview Behavior Observation</h5>
                            <p>{log.emotion === "Not Found Image" ? "—" : log.emotion}</p>
                          </div>
                        )}
                      </div>
                    );
                  }) ?? (
                    <div className="cr-report-empty-transcript"><p>No transcript data available</p></div>
                  )}
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Tab: JOB MATCHING SCORE
                • Candidate Info
                • Job Matching Score (ring + strengths/weaknesses/next_steps)
            ══════════════════════════════════════════════════════════════ */}
            {!generalQuestionSetMinimal && activeTab === "job_matching" && (
              <div className="cr-tab-content">
                {/* Candidate Info ────────────────────────────────────── */}
                {cd && (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <span className="cr-card-title">Candidate Info</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-candidate-info">
                      <div className="cr-candidate-avatar">{candidateInitials}</div>
                      <div className="cr-candidate-details">
                        <div className="cr-candidate-name">
                          {cd.firstname} {cd.lastname}
                        </div>
                        <div className="cr-candidate-meta">
                          {cd.email && <span>{cd.email}</span>}
                          {cd.phone && <span>{cd.phone}</span>}
                          {(cd as any).jobTitle && <span>{(cd as any).jobTitle}</span>}
                          {(cd as any).jobExp !== undefined && <span>ประสบการณ์ {(cd as any).jobExp} ปี</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Job Matching Score ─────────────────────────────────── */}
                {isLoadingScore ? (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <span className="cr-card-title">Job Matching Score</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-loading-inner">
                      <CircularProgress size={32} sx={{ color: "#4A25E1" }} />
                      <p>กำลังวิเคราะห์คะแนน...</p>
                    </div>
                  </div>
                ) : reportScoreData && reportScoreData.match_score > 0 ? (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">Job Matching Score</span>
                      <span className="cr-card-badge">ScoutOut AI</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-insight-body">
                        <div className="cr-score-row">
                          <ScoreRing score={reportScoreData.match_score} label="Match Score" size={110} />
                          <div className="cr-score-meta">
                            <span className={`cr-score-verdict cr-score-verdict-${reportScoreData.match_score >= 80 ? "high" : reportScoreData.match_score >= 60 ? "mid" : "low"}`}>
                              {scoreLabel(reportScoreData.match_score)}
                            </span>
                            <p className="cr-score-explanation">{reportScoreData.match_score_explanation}</p>
                          </div>
                        </div>

                        {(reportScoreData.strengths?.length > 0 || reportScoreData.weaknesses?.length > 0) && (
                          <div className="cr-verdict-grid">
                            {reportScoreData.strengths?.length > 0 && (
                              <div className="cr-verdict-card strength">
                                <div className="cr-verdict-header">
                                  <FontAwesomeIcon icon={faStar} />
                                  <span>จุดแข็ง</span>
                                </div>
                                <ul className="cr-verdict-list">
                                  {reportScoreData.strengths.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                              </div>
                            )}
                            {reportScoreData.weaknesses?.length > 0 && (
                              <div className="cr-verdict-card weakness">
                                <div className="cr-verdict-header">
                                  <FontAwesomeIcon icon={faTriangleExclamation} />
                                  <span>จุดที่ควรพัฒนา</span>
                                </div>
                                <ul className="cr-verdict-list">
                                  {reportScoreData.weaknesses.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {reportScoreData.next_steps && (
                          <div className="cr-next-steps">
                            <div className="cr-next-header">
                              <FontAwesomeIcon icon={faLightbulb} />
                              <span>คำแนะนำ / ขั้นตอนถัดไป</span>
                            </div>
                            {reportScoreData.next_steps.recommendation && (
                              <span className="cr-next-rec">{reportScoreData.next_steps.recommendation}</span>
                            )}
                            {reportScoreData.next_steps.detailed_advice && (
                              <p className="cr-next-advice">{reportScoreData.next_steps.detailed_advice}</p>
                            )}
                            {reportScoreData.next_steps.suggested_actions?.length > 0 && (
                              <ul className="cr-next-actions">
                                {reportScoreData.next_steps.suggested_actions.map((a, i) => <li key={i}>{a}</li>)}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                  </div>
                ) : (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <span className="cr-card-title">Job Matching Score</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-empty">
                      <img src={NO_REPORT} alt="" />
                      <p>ยังไม่มีข้อมูลคะแนน</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Tab: CUSTOM INSIGHT (custom measure vs HR criteria)
            ══════════════════════════════════════════════════════════════ */}
            {activeTab === "custom_insight" && (
              <div className="cr-tab-content">
                {!hasReport || !reportModel?.report_id ? (
                  <div className="cr-card">
                    <div className="cr-empty">
                      <p>ยังไม่พร้อมแสดงรายงาน</p>
                    </div>
                  </div>
                ) : customMeasureLoading && !customMeasureResult ? (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">Custom Insight</span>
                      <span className="cr-card-badge">ScoutOut AI</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-loading-inner">
                      <CircularProgress size={32} sx={{ color: "#4A25E1" }} />
                      <p>กำลังวิเคราะห์ตามเกณฑ์ของคุณ...</p>
                    </div>
                  </div>
                ) : customMeasureResult ? (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">Custom Insight</span>
                      <span className="cr-card-badge">ScoutOut AI</span>
                    </div>
                    <Divider className="cr-divider" />
                    <Box
                      sx={{
                        px: 2,
                        pb: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={compareDisabled}
                        onClick={() => void runCustomMeasureCompare()}
                        sx={{ borderColor: "#0f766e", color: "#0f766e" }}
                        startIcon={
                          compareLoading ? (
                            <CircularProgress size={16} sx={{ color: "#0f766e" }} />
                          ) : (
                            <FontAwesomeIcon icon={faArrowsLeftRight} />
                          )
                        }
                      >
                        {compareLoading ? "กำลังเปรียบเทียบ…" : "วิเคราะห์เปรียบเทียบ"}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={customMeasureLoading}
                        onClick={handleCustomMeasureReanalyze}
                        sx={{ borderColor: "#4A25E1", color: "#4A25E1" }}
                        startIcon={
                          customMeasureLoading ? (
                            <CircularProgress size={16} sx={{ color: "#4A25E1" }} />
                          ) : (
                            <FontAwesomeIcon icon={faRotateRight} />
                          )
                        }
                      >
                        {customMeasureLoading ? "กำลังวิเคราะห์…" : "วิเคราะห์ใหม่"}
                      </Button>
                    </Box>
                    <CustomMeasureAnalysisDetails
                      criteria={customMeasureResult.criteria}
                      analysis={customMeasureResult.analysis}
                    />
                  </div>
                ) : (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">Custom Insight</span>
                      <span className="cr-card-badge">ScoutOut AI</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-empty">
                      <img src={NO_REPORT} alt="" />
                      <p>ยังไม่มีผลวิเคราะห์ Custom Insight สำหรับผู้สมัครคนนี้</p>
                      <p style={{ fontSize: 14, color: "#64748b", maxWidth: 460, margin: "0 auto", lineHeight: 1.5 }}>
                        {jobCustomMeasurementCriteria?.trim()
                          ? "กดวิเคราะห์เพื่อประเมินคำตอบเทียบกับเกณฑ์ร่วมของตำแหน่งนี้ — เกณฑ์เดียวกันถูกตั้งที่ส่วนหัวของหน้าตำแหน่ง (การวัดผลร่วม)"
                          : "ยังไม่มีเกณฑ์การวัดผล — ตั้งค่า「การวัดผลร่วม (Custom Insight)」ที่ส่วนหัวของหน้าตำแหน่งก่อน แล้วค่อยกดวิเคราะห์ที่นี่"}
                      </p>
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          disabled={customMeasureLoading || !jobCustomMeasurementCriteria?.trim()}
                          onClick={() => void runCustomMeasureAnalysis({ switchTabOnSuccess: true })}
                          sx={{ backgroundColor: "#4A25E1", "&:hover": { backgroundColor: "#3b1db8" } }}
                          startIcon={
                            customMeasureLoading ? (
                              <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : undefined
                          }
                        >
                          {customMeasureLoading ? "กำลังวิเคราะห์…" : "วิเคราะห์"}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={compareDisabled}
                          onClick={() => void runCustomMeasureCompare()}
                          sx={{ borderColor: "#0f766e", color: "#0f766e" }}
                          startIcon={
                            compareLoading ? (
                              <CircularProgress size={16} sx={{ color: "#0f766e" }} />
                            ) : (
                              <FontAwesomeIcon icon={faArrowsLeftRight} />
                            )
                          }
                        >
                          {compareLoading ? "กำลังเปรียบเทียบ…" : "วิเคราะห์เปรียบเทียบ"}
                        </Button>
                      </Box>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showGeneralObjectiveTab && activeTab === "general_objective" && (
              <div className="cr-tab-content">
                {!hasReport || !reportModel?.report_id ? (
                  <div className="cr-card">
                    <div className="cr-empty">
                      <p>ยังไม่พร้อมแสดงรายงาน</p>
                    </div>
                  </div>
                ) : generalObjectiveLoading && !generalObjectiveResult ? (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">วิเคราะห์ Objective (Generative AI)</span>
                      <span className="cr-card-badge">ScoutOut AI</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-loading-inner">
                      <CircularProgress size={32} sx={{ color: "#4A25E1" }} />
                      <p>กำลังวิเคราะห์คำตอบเทียบ Objective ของชุดคำถาม…</p>
                    </div>
                  </div>
                ) : generalObjectiveResult ? (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">วิเคราะห์ Objective</span>
                      <span className="cr-card-badge">ScoutOut AI</span>
                    </div>
                    <Divider className="cr-divider" />
                    <Box sx={{ px: 2, pb: 1, display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={generalObjectiveLoading}
                        onClick={() => void runGeneralObjectiveAnalysis({ force: true })}
                        sx={{ borderColor: "#4A25E1", color: "#4A25E1" }}
                        startIcon={
                          generalObjectiveLoading ? (
                            <CircularProgress size={16} sx={{ color: "#4A25E1" }} />
                          ) : (
                            <FontAwesomeIcon icon={faRotateRight} />
                          )
                        }
                      >
                        {generalObjectiveLoading ? "กำลังวิเคราะห์…" : "วิเคราะห์ใหม่"}
                      </Button>
                    </Box>
                    <GeneralObjectiveInsightDisplay
                      objectivePreview={String(generalObjectiveText || "").trim()}
                      analysis={generalObjectiveResult}
                    />
                  </div>
                ) : (
                  <div className="cr-card">
                    <div className="cr-card-header">
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">วิเคราะห์ Objective</span>
                      <span className="cr-card-badge">ScoutOut AI</span>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-empty">
                      <img src={NO_REPORT} alt="" />
                      <p>ยังไม่มีผลวิเคราะห์ Objective สำหรับผู้สมัครคนนี้</p>
                      <p style={{ fontSize: 14, color: "#64748b", maxWidth: 520, margin: "0 auto", lineHeight: 1.5 }}>
                        ระบบจะจับคู่ <b>Objective ของชุดคำถามทั่วไป</b> กับคำถามและคำตอบในแบบสอบถามของผู้สมัคร
                        แล้วสรุปความสอดคล้อง จุดแข็ง และข้อเสนอแนะ — กดปุ่มด้านล่างเพื่อรัน Generative AI
                      </p>
                      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={generalObjectiveLoading}
                          onClick={() => void runGeneralObjectiveAnalysis({ switchTabOnSuccess: true, force: false })}
                          sx={{ backgroundColor: "#4A25E1", "&:hover": { backgroundColor: "#3b1db8" } }}
                          startIcon={
                            generalObjectiveLoading ? (
                              <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : undefined
                          }
                        >
                          {generalObjectiveLoading ? "กำลังวิเคราะห์…" : "วิเคราะห์ด้วย AI"}
                        </Button>
                      </Box>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Tab: PROFI REPORT (app_id PROFI)
            ══════════════════════════════════════════════════════════════ */}
            {!generalQuestionSetMinimal && activeTab === "profi" && isProfiJob && (
              <div className="cr-tab-content">
                {profiSnapshotLoading ? (
                  <div className="cr-card">
                    <div className="cr-loading-inner">
                      <CircularProgress size={32} sx={{ color: "#4A25E1" }} />
                      <p>กำลังโหลด ProFi Report...</p>
                    </div>
                  </div>
                ) : profiSnapshotStatus === "ready" && profiReportData ? (
                  <div className="cr-card cr-profi-public-card">
                    <div className="cr-profi-report-header">
                      <div className="cr-profi-report-header-left">
                        <div className="cr-profi-report-header-row">
                          <h1 className="cr-profi-main-heading">ผลการวิเคราะห์ ProFi</h1>
                          <span className="cr-profi-status-badge">วิเคราะห์แล้ว</span>
                          {profiPublicReportUrl && (
                            <Button
                              component="a"
                              href={profiPublicReportUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="outlined"
                              size="small"
                              sx={{ marginLeft: "auto", borderColor: "#4A25E1", color: "#4A25E1" }}
                            >
                              <FontAwesomeIcon icon={faUpRightFromSquare} style={{ marginRight: 8 }} />
                              ดูรายงานฝั่งผู้สมัคร
                            </Button>
                          )}
                        </div>
                        <p className="cr-profi-sub-heading">
                          ข้อเสนอแนะจากการสัมภาษณ์ — แสดงชุดเดียวกับที่ผู้สมัครเห็นบน ProFi
                        </p>
                        {profiHiringDisplay != null && (
                          <div className="cr-profi-hiring-badge" aria-label="คะแนนประเมิน">
                            <span className="cr-profi-hiring-label">คะแนนประเมินโอกาสได้งาน</span>
                            <span className="cr-profi-hiring-value">{Math.round(profiHiringDisplay)}</span>
                            <span className="cr-profi-hiring-max">/ 100</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-insight-body cr-profi-public-body">
                      {!profiHasDisplayableContent(profiReportData) ? (
                        <div className="cr-profi-empty-state">
                          <p>ยังไม่มีข้อมูลข้อเสนอแนะในรายงานนี้</p>
                        </div>
                      ) : (
                        <>
                          {profiOverallTrim.length > 0 && (
                            <section className="cr-profi-section">
                              <h2 className="cr-profi-section-title">
                                <span className="cr-profi-section-emoji" aria-hidden>
                                  💡
                                </span>{" "}
                                ข้อเสนอแนะภาพรวม
                              </h2>
                              <div className="cr-profi-overall-advice">{profiOverallTrim}</div>
                            </section>
                          )}
                          {profiDisplayLogs.length > 0 && (
                            <section className="cr-profi-section">
                              <h2 className="cr-profi-section-title">
                                <span className="cr-profi-section-emoji" aria-hidden>
                                  📋
                                </span>{" "}
                                ข้อเสนอแนะรายคำถาม
                              </h2>
                              <div className="cr-profi-feedback-grid">
                                {profiDisplayLogs.map((log, index) => (
                                  <div
                                    key={log.question_id || `q-${index}`}
                                    className="cr-profi-feedback-card"
                                  >
                                    <div className="cr-profi-q-section">
                                      <div className="cr-profi-q-badge">คำถามที่ {index + 1}</div>
                                      <div className="cr-profi-q-text">
                                        {log.question?.th || log.question?.en || "—"}
                                      </div>
                                    </div>
                                    <div className="cr-profi-fb-section">
                                      <div className="cr-profi-fb-header">ข้อเสนอแนะจาก AI:</div>
                                      <div className="cr-profi-fb-text">{log.feedback || "—"}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}
                          {profiDisplayLogs.length === 0 && !profiOverallTrim && profiHiringDisplay != null && (
                            <p className="cr-profi-hiring-only-note">
                              มีเฉพาะคะแนนประเมิน — รายละเอียดข้อเสนอแนะจะแสดงเมื่อระบบประมวลผลครบ
                            </p>
                          )}
                        </>
                      )}
                      {profiReportData.updated_at && (
                        <p className="cr-profi-updated-at">
                          อัปเดตล่าสุด: {String(profiReportData.updated_at)}
                        </p>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={isReanalyzingProfi}
                        onClick={handleProfiReanalyze}
                        sx={{ borderColor: "#4A25E1", color: "#4A25E1" }}
                      >
                        <FontAwesomeIcon icon={faRotateRight} style={{ marginRight: 8 }} />
                        {isReanalyzingProfi ? "กำลังวิเคราะห์..." : "วิเคราะห์ใหม่ / ส่งอีเมลอีกครั้ง"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="cr-card">
                    <div
                      className="cr-card-header"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <img src={AI_PURPLE} className="cr-card-icon" alt="" />
                      <span className="cr-card-title">ProFi</span>
                      <span className="cr-card-badge" style={{ background: "#fef3c7", color: "#92400e" }}>
                        {profiSnapshotStatus === "processing" ? "กำลังประมวลผล" : "ยังไม่พร้อม / รอวิเคราะห์"}
                      </span>
                      {profiPublicReportUrl && (
                        <Button
                          component="a"
                          href={profiPublicReportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="small"
                          sx={{ marginLeft: "auto", borderColor: "#4A25E1", color: "#4A25E1" }}
                        >
                          <FontAwesomeIcon icon={faUpRightFromSquare} style={{ marginRight: 8 }} />
                          ดูรายงานฝั่งผู้สมัคร
                        </Button>
                      )}
                    </div>
                    <Divider className="cr-divider" />
                    <div className="cr-insight-body">
                      <Alert
                        severity={profiSnapshotStatus === "processing" ? "info" : "warning"}
                        sx={{ mb: 2 }}
                      >
                        {profiSnapshotError || "ยังไม่มีผลวิเคราะห์ ProFi ในระบบ"}
                      </Alert>
                      <Button
                        variant="contained"
                        className="button-std"
                        disabled={isReanalyzingProfi}
                        onClick={handleProfiReanalyze}
                      >
                        <FontAwesomeIcon icon={faRotateRight} style={{ marginRight: 8 }} />
                        {isReanalyzingProfi ? "กำลังวิเคราะห์..." : "Analyze Again"}
                      </Button>
                      <p style={{ marginTop: 16, fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
                        เรียก Recommend API ใหม่ บันทึกลง <code>interview_report_profi</code>{" "}
                        และส่งอีเมลแจ้ง candidate เมื่อสถานะ READY (ถ้ามีอีเมลในระบบ)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
      {isShowSendDialog && (
        <ConfirmGeneralModal
          icon={NOTI_PURPLE}
          buttonCancel="ยกเลิก" buttonConfirm="ยืนยัน"
          message="ยืนยันที่จะส่งแจ้งเตือนผู้สมัคร?"
          message2="ระบบจะทำการส่งลิงก์การสัมภาษณ์ให้ผู้สมัครอีกครั้ง ยืนยันที่จะส่ง?"
          onConfrim={handleSendEmail}
          isOpen={isShowSendDialog} onClose={() => setIsShowSendDialog(false)}
        />
      )}
      {isShowAIDetectDialog && (
        <ConfirmGeneralModal
          icon={AI_PURPLE}
          buttonCancel="ยกเลิก" buttonConfirm="ยืนยัน"
          message="ยืนยันที่จะตรวจสอบการใช้ AI?"
          message2="ระบบจะวิเคราะห์คำตอบของผู้สมัครจากข้อมูลในรายงาน กระบวนการนี้อาจใช้เวลาสักครู่"
          onConfrim={handleDetectAI}
          isOpen={isShowAIDetectDialog} onClose={() => setIsShowAIDetectDialog(false)}
        />
      )}

      <Dialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        aria-labelledby="cr-compare-dialog-title"
      >
        <DialogTitle id="cr-compare-dialog-title">เปรียบเทียบผล Custom Insight: Gemini vs OpenAI</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ใช้ prompt และเกณฑ์เดียวกัน รันคู่ขนานทั้งสองโมเดล — ไม่บันทึกทับผลวิเคราะห์ที่เก็บในระบบ (แยกจากปุ่ม「วิเคราะห์」ปกติ)
          </Typography>
          {comparePayload ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomMeasureCompareColumn
                  title="Google Gemini"
                  accentColor="#1a73e8"
                  result={comparePayload.gemini}
                  criteria={comparePayload.criteria}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomMeasureCompareColumn
                  title="OpenAI"
                  accentColor="#10a37f"
                  result={comparePayload.openai}
                  criteria={comparePayload.criteria}
                />
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)} variant="contained" color="inherit">
            ปิด
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={snackbar.open} message={snackbar.message}
        severity={snackbar.severity} duration={snackbar.duration}
        onClose={hideSnackbar}
      />
    </>
  );
};

export default CandidateReport;
